import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from 'context/AuthContext';
import axiosInstance, { refreshSession, clearSessionState } from 'utils/axiosConfig';
import { FAST_API_BASE_URL } from 'constants';

const MINUTES = (m) => m * 60 * 1000;
const SECONDS = (s) => s * 1000;

const IDLE_TIMEOUT_MINUTES        = 15;
const WARNING_SHOWN_AT_MINUTE     = 13;
const WARNING_COUNTDOWN_SECONDS   = (IDLE_TIMEOUT_MINUTES - WARNING_SHOWN_AT_MINUTE) * 60;

const SHOW_WARNING_AFTER_MS  = MINUTES(WARNING_SHOWN_AT_MINUTE);
const AUTO_LOGOUT_AFTER_MS   = MINUTES(IDLE_TIMEOUT_MINUTES);

const USER_ACTIVITY_EVENTS = [
  'mousedown', 'mousemove', 'keydown', 'scroll',
  'touchstart', 'click', 'input', 'focus', 'wheel',
];


export const useSessionManager = () => {
  const { isAuthenticated, logout } = useAuth();

  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const warningTimerRef   = useRef(null);
  const logoutTimerRef    = useRef(null);
  const countdownTimerRef = useRef(null);
  const isWarningActiveRef = useRef(false);

  const clearAllTimers = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownTimerRef.current);

    warningTimerRef.current   = null;
    logoutTimerRef.current    = null;
    countdownTimerRef.current = null;
  }, []);

  const hideWarning = useCallback(() => {
    isWarningActiveRef.current = false;
    setIsWarningVisible(false);
    setCountdownSeconds(0);
  }, []);

  const isRememberMeEnabled = () => localStorage.getItem('rememberMe') === 'true';

  const logoutDueToInactivity = useCallback(async () => {
    clearAllTimers();
    hideWarning();

    try {
      await axiosInstance.post(
        `${FAST_API_BASE_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch {
  
    }

    clearSessionState();
    logout();
    window.location.href = '/login';
  }, [clearAllTimers, hideWarning, logout]);

  const showInactivityWarning = useCallback(() => {
    if (!isAuthenticated())     return;
    if (isRememberMeEnabled())  return;

    isWarningActiveRef.current = true;
    setCountdownSeconds(WARNING_COUNTDOWN_SECONDS);
    setIsWarningVisible(true);

    let secondsLeft = WARNING_COUNTDOWN_SECONDS;

    countdownTimerRef.current = setInterval(() => {
      secondsLeft -= 1;
      setCountdownSeconds(secondsLeft);

      if (secondsLeft <= 0) {
        clearInterval(countdownTimerRef.current);
        logoutDueToInactivity();
      }
    }, SECONDS(1));
  }, [isAuthenticated, logoutDueToInactivity]);

  const restartInactivityTimer = useCallback(() => {
    if (!isAuthenticated())        return;
    if (isWarningActiveRef.current) return;

    clearAllTimers();

    warningTimerRef.current = setTimeout(showInactivityWarning, SHOW_WARNING_AFTER_MS);
    logoutTimerRef.current  = setTimeout(logoutDueToInactivity,  AUTO_LOGOUT_AFTER_MS);
  }, [isAuthenticated, showInactivityWarning, logoutDueToInactivity, clearAllTimers]);



  const extendSession = useCallback(() => {
    clearAllTimers();
    hideWarning();

    refreshSession().catch(console.error);
    restartInactivityTimer();
  }, [clearAllTimers, hideWarning, restartInactivityTimer]);


  useEffect(() => {
    if (!isAuthenticated()) return;

    USER_ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, restartInactivityTimer, { passive: true })
    );

    restartInactivityTimer();

    return () => {
      USER_ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, restartInactivityTimer)
      );
      clearAllTimers();
    };
  }, [isAuthenticated, restartInactivityTimer, clearAllTimers]);


  return {
    isWarningVisible,
    countdownSeconds,
    extendSession,
  };
};